(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const makeOption = (value, label = value) => {
    const option = document.createElement('option');
    option.value = value;
    option.textContent = label;
    return option;
  };

  ready(() => {
    const header = document.querySelector('[data-pv2-header]');
    const toggle = document.querySelector('[data-pv2-menu-toggle]');
    const mobileMenu = document.querySelector('[data-pv2-mobile-menu]');
    const menuBackdrop = document.querySelector('[data-pv2-menu-backdrop]');
    const menuClose = document.querySelector('[data-pv2-menu-close]');
    const restartPullback = (slide, reducedMotion) => {
      const image = slide?.querySelector('img');
      if (!image) return;
      image.classList.remove('pv2-is-pulling-back');
      if (reducedMotion) return;
      window.requestAnimationFrame(() => {
        if (slide.classList.contains('is-active')) image.classList.add('pv2-is-pulling-back');
      });
    };
    const syncHeader = () => {
      if (header) header.classList.toggle('is-solid', window.scrollY > 32);
    };

    syncHeader();
    window.addEventListener('scroll', syncHeader, { passive: true });

    if (toggle && mobileMenu) {
      const setMenuOpen = (open) => {
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        mobileMenu.classList.toggle('is-open', open);
        mobileMenu.setAttribute('aria-hidden', String(!open));
        menuBackdrop?.classList.toggle('is-open', open);
        document.documentElement.classList.toggle('pv2-menu-is-open', open);
        document.documentElement.style.overflow = open ? 'hidden' : '';
      };

      toggle.addEventListener('click', () => {
        setMenuOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });

      menuBackdrop?.addEventListener('click', () => setMenuOpen(false));
      menuClose?.addEventListener('click', () => setMenuOpen(false));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
          setMenuOpen(false);
          toggle.focus();
        }
      });

      mobileMenu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          setMenuOpen(false);
        });
      });
    }

    const jumpToSection = (targetId, event) => {
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;
      event?.preventDefault();
      window.history.pushState(null, '', `#${encodeURIComponent(targetId)}`);
      const moveToTarget = (behavior = 'smooth') => target.scrollIntoView({ behavior, block: 'start' });
      moveToTarget();
      window.setTimeout(() => {
        if (window.location.hash === `#${encodeURIComponent(targetId)}` && Math.abs(target.getBoundingClientRect().top) > 180) {
          moveToTarget('auto');
        }
      }, 700);
    };

    document.querySelectorAll('.pv4-hero-paths a[href^="#"], .pv4-boutique__links a[href^="#"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        jumpToSection(decodeURIComponent(link.getAttribute('href')?.slice(1) || ''), event);
      });
    });

    document.querySelectorAll('[data-pv4-jump]').forEach((card) => {
      const targetId = decodeURIComponent(card.dataset.pv4Jump?.replace(/^#/, '') || '');
      card.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        jumpToSection(targetId, event);
      });
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        jumpToSection(targetId, event);
      });
    });

    document.querySelectorAll('[data-pv2-hero]').forEach((hero) => {
      const slides = [...hero.querySelectorAll('[data-pv2-slide]')];
      const dots = [...hero.querySelectorAll('[data-pv2-dot]')];
      const previous = hero.querySelector('[data-pv2-prev]');
      const next = hero.querySelector('[data-pv2-next]');
      const caption = hero.querySelector('[data-pv2-caption]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let active = 0;
      let timer;
      let touchStart = 0;

      if (!slides.length) return;

      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, position) => {
          const selected = position === active;
          slide.classList.toggle('is-active', selected);
          slide.setAttribute('aria-hidden', String(!selected));
        });
        dots.forEach((dot, position) => {
          const selected = position === active;
          dot.classList.toggle('is-active', selected);
          dot.setAttribute('aria-current', selected ? 'true' : 'false');
        });
        if (caption) caption.textContent = slides[active].dataset.caption || '';
        restartPullback(slides[active], reducedMotion);
      };

      const restart = () => {
        window.clearInterval(timer);
        if (!reducedMotion && slides.length > 1) {
          timer = window.setInterval(() => show(active + 1), 6500);
        }
      };

      previous?.addEventListener('click', () => {
        show(active - 1);
        restart();
      });
      next?.addEventListener('click', () => {
        show(active + 1);
        restart();
      });
      dots.forEach((dot, position) => dot.addEventListener('click', () => {
        show(position);
        restart();
      }));
      hero.addEventListener('touchstart', (event) => {
        touchStart = event.changedTouches[0]?.clientX || 0;
      }, { passive: true });
      hero.addEventListener('touchend', (event) => {
        const delta = (event.changedTouches[0]?.clientX || 0) - touchStart;
        if (Math.abs(delta) > 45) {
          show(active + (delta < 0 ? 1 : -1));
          restart();
        }
      }, { passive: true });
      hero.addEventListener('mouseenter', () => window.clearInterval(timer));
      hero.addEventListener('mouseleave', restart);
      show(0);
      restart();
    });

    document.querySelectorAll('[data-pv2-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('[data-pv2-gallery-track]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let timer;
      if (!track) return;

      const step = () => {
        const firstItem = track.querySelector('.pv2-gallery__item');
        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 16;
        return firstItem ? firstItem.getBoundingClientRect().width + gap : Math.min(track.clientWidth * 0.82, 560);
      };

      const move = (direction = 1) => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        if (direction > 0 && track.scrollLeft >= maxScroll - 8) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else if (direction < 0 && track.scrollLeft <= 8) {
          track.scrollTo({ left: maxScroll, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: step() * direction, behavior: 'smooth' });
        }
      };

      const restart = () => {
        window.clearInterval(timer);
        if (!reducedMotion && track.children.length > 1) timer = window.setInterval(() => move(1), 3000);
      };

      gallery.querySelector('[data-pv2-gallery-prev]')?.addEventListener('click', () => {
        move(-1);
        restart();
      });
      gallery.querySelector('[data-pv2-gallery-next]')?.addEventListener('click', () => {
        move(1);
        restart();
      });
      gallery.addEventListener('mouseenter', () => window.clearInterval(timer));
      gallery.addEventListener('mouseleave', restart);
      gallery.addEventListener('focusin', () => window.clearInterval(timer));
      gallery.addEventListener('focusout', restart);
      restart();
    });

    document.querySelectorAll('[data-pv2-auto-carousel]').forEach((carousel) => {
      const slides = [...carousel.querySelectorAll('[data-pv2-carousel-slide]')];
      const dots = [...carousel.querySelectorAll('[data-pv2-carousel-dot]')];
      const previous = carousel.querySelector('[data-pv2-carousel-prev]');
      const next = carousel.querySelector('[data-pv2-carousel-next]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const interval = Number.parseInt(carousel.dataset.interval, 10) || 4500;
      let active = 0;
      let timer;

      if (slides.length < 2) return;

      const show = (index) => {
        active = (index + slides.length) % slides.length;
        slides.forEach((slide, position) => {
          const selected = position === active;
          slide.classList.toggle('is-active', selected);
          slide.setAttribute('aria-hidden', String(!selected));
        });
        dots.forEach((dot, position) => {
          const selected = position === active;
          dot.classList.toggle('is-active', selected);
          dot.setAttribute('aria-current', selected ? 'true' : 'false');
        });
        restartPullback(slides[active], reducedMotion);
      };

      const restart = () => {
        window.clearInterval(timer);
        if (!reducedMotion) timer = window.setInterval(() => show(active + 1), interval);
      };

      dots.forEach((dot, position) => dot.addEventListener('click', () => {
        show(position);
        restart();
      }));
      previous?.addEventListener('click', () => {
        show(active - 1);
        restart();
      });
      next?.addEventListener('click', () => {
        show(active + 1);
        restart();
      });
      carousel.addEventListener('mouseenter', () => window.clearInterval(timer));
      carousel.addEventListener('mouseleave', restart);
      carousel.addEventListener('focusin', () => window.clearInterval(timer));
      carousel.addEventListener('focusout', restart);
      show(0);
      restart();
    });

    const catalogue = window.PatrimonioCatalogueData;
    document.querySelectorAll('[data-pv2-producers]').forEach((section) => {
      if (!catalogue?.producers?.length) return;
      const regionButtons = [...section.querySelectorAll('[data-region]')];
      const regionTitle = section.querySelector('[data-pv2-region-title]');
      const list = section.querySelector('[data-pv2-producer-list]');
      const iconTemplate = section.querySelector('[data-pv2-producer-icon]');
      const arrowTemplate = section.querySelector('[data-pv2-producer-arrow]');

      const showRegion = (region) => {
        const producers = catalogue.producers.filter((producer) => producer.region === region);
        regionButtons.forEach((button) => {
          const selected = button.dataset.region === region;
          button.classList.toggle('is-active', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
        if (regionTitle) regionTitle.textContent = region;
        if (!list) return;
        list.replaceChildren();
        producers.forEach((producer, position) => {
          const item = document.createElement('li');
          item.className = 'pv2-producer-panel__item';
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'pv2-producer-panel__button';
          button.dataset.producer = producer.name;
          button.setAttribute('aria-expanded', 'false');

          const detailsId = `pv2-producer-${region.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${position + 1}`;
          button.setAttribute('aria-controls', detailsId);

          const identity = document.createElement('span');
          identity.className = 'pv2-producer-panel__identity';
          const icon = document.createElement('span');
          icon.className = 'pv2-producer-panel__icon';
          if (iconTemplate) icon.append(iconTemplate.content.cloneNode(true));
          const name = document.createElement('span');
          name.textContent = producer.name;
          identity.append(icon, name);

          const arrow = document.createElement('span');
          arrow.className = 'pv2-producer-panel__toggle-icon';
          if (arrowTemplate) arrow.append(arrowTemplate.content.cloneNode(true));
          button.append(identity, arrow);

          const details = document.createElement('div');
          details.id = detailsId;
          details.className = 'pv2-producer-panel__details';
          details.hidden = true;
          const story = document.createElement('p');
          story.textContent = producer.story || 'Producer profile and related wines are ready for expansion.';
          details.append(story);

          button.addEventListener('click', () => {
            const willOpen = button.getAttribute('aria-expanded') !== 'true';
            list.querySelectorAll('.pv2-producer-panel__button').forEach((entry) => {
              entry.classList.remove('is-active');
              entry.setAttribute('aria-expanded', 'false');
            });
            list.querySelectorAll('.pv2-producer-panel__details').forEach((entry) => {
              entry.hidden = true;
            });
            if (willOpen) {
              button.classList.add('is-active');
              button.setAttribute('aria-expanded', 'true');
              details.hidden = false;
            }
          });

          item.append(button, details);
          list.append(item);
        });
      };

      regionButtons.forEach((button) => button.addEventListener('click', () => showRegion(button.dataset.region)));
      showRegion(regionButtons[0]?.dataset.region || catalogue.regions[0]?.name || 'Champagne');
    });

    document.querySelectorAll('[data-pv2-catalogue]').forEach((section) => {
      if (!catalogue?.wines?.length) return;
      const grid = section.querySelector('[data-pv2-wine-grid]');
      const search = section.querySelector('[data-pv2-search]');
      const region = section.querySelector('[data-pv2-region-filter]');
      const producer = section.querySelector('[data-pv2-producer-filter]');
      const vintage = section.querySelector('[data-pv2-vintage-filter]');
      const type = section.querySelector('[data-pv2-type-filter]');
      const more = section.querySelector('[data-pv2-more]');
      const detail = section.querySelector('[data-pv2-wine-detail]');
      const detailClose = section.querySelector('[data-pv2-detail-close]');
      const detailBackdrop = section.querySelector('[data-pv2-detail-backdrop]');
      const detailImage = section.querySelector('[data-pv2-detail-image]');
      const detailPlaceholder = section.querySelector('[data-pv2-detail-placeholder]');
      const detailRegion = section.querySelector('[data-pv2-detail-region]');
      const detailName = section.querySelector('[data-pv2-detail-name]');
      const detailProducer = section.querySelector('[data-pv2-detail-producer]');
      const detailVintage = section.querySelector('[data-pv2-detail-vintage]');
      const detailSize = section.querySelector('[data-pv2-detail-size]');
      const detailStory = section.querySelector('[data-pv2-detail-story]');
      const detailPrice = section.querySelector('[data-pv2-detail-price]');
      const quantity = section.querySelector('[data-pv2-quantity]');
      const quantityMinus = section.querySelector('[data-pv2-quantity-minus]');
      const quantityPlus = section.querySelector('[data-pv2-quantity-plus]');
      const addOrder = section.querySelector('[data-pv2-add-order]');
      const orderStatus = section.querySelector('[data-pv2-order-status]');
      const orderCart = section.querySelector('[data-pv2-order-cart]');
      const orderCartBackdrop = section.querySelector('[data-pv2-order-cart-backdrop]');
      const orderCartClose = section.querySelector('[data-pv2-order-cart-close]');
      const orderCartItems = section.querySelector('[data-pv2-order-cart-items]');
      const orderCartEmpty = section.querySelector('[data-pv2-order-cart-empty]');
      const orderSubtotal = section.querySelector('[data-pv2-order-subtotal]');
      const orderGst = section.querySelector('[data-pv2-order-gst]');
      const orderDelivery = section.querySelector('[data-pv2-order-delivery]');
      const orderTotal = section.querySelector('[data-pv2-order-total]');
      const orderCheckout = section.querySelector('[data-pv2-order-checkout]');
      const orderSetup = section.querySelector('[data-pv2-order-setup]');
      const showPrices = section.dataset.showPrices !== 'false';
      const imageDataElement = section.querySelector('[data-pv2-wine-images]');
      const producerStories = new Map((catalogue.producers || []).map((item) => [item.name, item.story]));
      let wineImages = {};
      let selectedKey = '';
      let selectedWine = null;
      let visible = 8;
      let shopifyCart = { items: [], item_count: 0, items_subtotal_price: 0 };
      const shopifyVariants = new Map();
      let cartReturnFocus = null;

      try {
        wineImages = JSON.parse(imageDataElement?.textContent || '{}');
      } catch (error) {
        console.warn('Patrimonio wine image map could not be parsed.', error);
      }

      const wineKey = (wine) => `${wine.producer}|${wine.name}`;
      const wineVariantImageKey = (wine) => `${wine.producer}|${wine.name}|${wine.type}`;
      const normalizeWineName = (value) => String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[’']/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
      const imagePendingWineNames = new Set([]);
      const normalizedProductKey = (producerName, wineName, wineVintage, wineSize, wineType) => [
        producerName,
        wineName,
        wineVintage,
        wineSize,
        wineType
      ].map((value) => String(value || '').trim().toLocaleLowerCase('en')).join('|');
      const formatPrice = (price) => new Intl.NumberFormat('en-SG', {
        style: 'currency',
        currency: 'SGD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(price));
      const formatPriceCents = (cents) => formatPrice(Number(cents || 0) / 100);

      const imageFor = (wine) => imagePendingWineNames.has(normalizeWineName(wine?.name))
        ? {}
        : wineImages[wineVariantImageKey(wine)] || wineImages[wineKey(wine)] || {};
      const isImageProvided = (image) => Boolean(image?.src)
        && image.match !== 'placeholder'
        && !image.src.includes('placeholder');
      const clampQuantity = (value) => Math.max(1, Math.min(12, Number.parseInt(value, 10) || 1));
      const variantIdFor = (wine) => Number.parseInt(
        wine?.variantId
          || wine?.variant_id
          || shopifyVariants.get(`sku:${wine?.sku || ''}`)
          || window.PatrimonioVariantMap?.[wineKey(wine)]
          || shopifyVariants.get(normalizedProductKey(wine?.producer, wine?.name, wine?.vintage, wine?.size, wine?.type))
          || '',
        10
      );

      const loadShopifyProductMap = async () => {
        const pages = await Promise.all([1, 2].map(async (page) => {
          const response = await fetch(`/products.json?limit=250&page=${page}`, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin'
          });
          if (!response.ok) throw new Error(`Product request failed (${response.status})`);
          const payload = await response.json();
          return payload.products || [];
        }));

        pages.flat().forEach((product) => {
          (product.variants || []).forEach((variant) => {
            const key = normalizedProductKey(product.vendor, product.title, variant.option1, variant.option2, variant.option3);
            if (variant.id) shopifyVariants.set(key, Number(variant.id));
            if (variant.id && variant.sku) shopifyVariants.set(`sku:${variant.sku}`, Number(variant.id));
          });
        });
        return shopifyVariants;
      };

      const syncPageLock = () => {
        const locked = (detail && !detail.hidden) || (orderCart && !orderCart.hidden) || mobileMenu?.classList.contains('is-open');
        document.documentElement.style.overflow = locked ? 'hidden' : '';
      };

      const updateCartCounts = () => {
        document.querySelectorAll('[data-pv2-cart-count]').forEach((element) => {
          element.textContent = String(shopifyCart.item_count || 0);
        });
      };

      const loadShopifyCart = async () => {
        const response = await fetch('/cart.js', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        shopifyCart = await response.json();
        updateCartCounts();
        return shopifyCart;
      };

      const renderOrderCart = () => {
        if (orderCartItems) orderCartItems.replaceChildren();
        const items = shopifyCart.items || [];
        const subtotal = Number(shopifyCart.items_subtotal_price || shopifyCart.total_price || 0);
        const gst = Math.round(subtotal * 0.09);
        const delivery = subtotal > 0 && subtotal < 30000 ? 5000 : 0;
        const estimatedTotal = subtotal + gst + delivery;
        if (orderCartEmpty) orderCartEmpty.hidden = items.length > 0;
        if (orderSubtotal) orderSubtotal.textContent = formatPriceCents(subtotal);
        if (orderGst) orderGst.textContent = formatPriceCents(gst);
        if (orderDelivery) orderDelivery.textContent = subtotal >= 30000 ? 'Free' : formatPriceCents(delivery);
        if (orderTotal) orderTotal.textContent = formatPriceCents(estimatedTotal);

        items.forEach((item) => {
          const listItem = document.createElement('li');
          listItem.className = 'pv2-order-cart__item';
          const media = item.image ? document.createElement('img') : null;
          if (item.image) {
            media.src = item.image;
            media.alt = `${item.product_title || item.title} bottle`;
            media.width = 70;
            media.height = 100;
          } else {
            listItem.classList.add('pv2-order-cart__item--no-media');
          }
          const copy = document.createElement('div');
          copy.className = 'pv2-order-cart__item-copy';
          const name = document.createElement('h4');
          name.textContent = item.product_title || item.title || 'Wine';
          const description = document.createElement('p');
          description.className = 'pv2-order-cart__item-meta';
          const variantLabel = item.variant_title && item.variant_title !== 'Default Title'
            ? item.variant_title
            : '';
          description.textContent = [
            variantLabel,
            `Quantity: ${item.quantity}`,
            `${formatPriceCents(item.final_price)} each`
          ].filter(Boolean).join(' · ');
          const removeButton = document.createElement('button');
          removeButton.type = 'button';
          removeButton.textContent = 'Remove';
          removeButton.addEventListener('click', async () => {
            removeButton.disabled = true;
            try {
              const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ id: item.key, quantity: 0 })
              });
              if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
              shopifyCart = await response.json();
              updateCartCounts();
              renderOrderCart();
            } catch (error) {
              removeButton.disabled = false;
              if (orderSetup) orderSetup.textContent = 'The cart could not be updated. Please try again.';
              console.warn('Patrimonio Shopify cart update failed.', error);
            }
          });
          const lineTotal = document.createElement('strong');
          lineTotal.className = 'pv2-order-cart__line-total';
          lineTotal.setAttribute('aria-label', `Line total for ${name.textContent}`);
          lineTotal.textContent = formatPriceCents(item.final_line_price);
          copy.append(name, description, removeButton);
          if (media) listItem.append(media);
          listItem.append(copy, lineTotal);
          orderCartItems?.append(listItem);
        });

        const canCheckout = items.length > 0;
        orderCheckout?.setAttribute('aria-disabled', String(!canCheckout));
        orderCheckout?.classList.toggle('is-disabled', !canCheckout);
        if (orderSetup) {
          orderSetup.textContent = canCheckout
            ? 'Secure payment, final GST and delivery charges are confirmed in Shopify checkout.'
            : 'Your Shopify cart is empty. Wines can be purchased online once their Shopify product records are connected.';
        }
      };

      const closeOrderCart = () => {
        if (orderCart) orderCart.hidden = true;
        if (orderCartBackdrop) orderCartBackdrop.hidden = true;
        syncPageLock();
        const fallbackCartLink = document.querySelector('[data-pv2-cart-open]');
        const focusTarget = cartReturnFocus?.isConnected ? cartReturnFocus : fallbackCartLink;
        focusTarget?.focus({ preventScroll: true });
        cartReturnFocus = null;
      };

      const openOrderCart = async (trigger = null) => {
        cartReturnFocus = trigger || document.querySelector('[data-pv2-cart-open]');
        if (mobileMenu?.classList.contains('is-open') && toggle?.getAttribute('aria-expanded') === 'true') {
          toggle.click();
        }
        if (detail && !detail.hidden) closeDetail();
        try {
          await loadShopifyCart();
        } catch (error) {
          console.warn('Patrimonio Shopify cart could not be loaded.', error);
          if (orderSetup) orderSetup.textContent = 'The Shopify cart could not be loaded. Please refresh and try again.';
        }
        renderOrderCart();
        if (orderCart) orderCart.hidden = false;
        if (orderCartBackdrop) orderCartBackdrop.hidden = false;
        if (orderCart) orderCart.scrollTop = 0;
        syncPageLock();
        orderCartClose?.focus({ preventScroll: true });
      };

      const unique = (items) => [...new Set(items.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));

      const renderWineDetailName = (element, value) => {
        if (!element) return;
        const name = String(value || '').trim();
        const parentheticalMatch = name.match(/^(.*?)\s*(\([^()]*\))\s*$/);
        const main = (parentheticalMatch?.[1] || name).trim();
        const qualifier = (parentheticalMatch?.[2] || '').trim();

        element.replaceChildren();
        element.classList.toggle('has-qualifier', Boolean(qualifier));
        element.classList.toggle('is-long', main.length > 36);
        element.classList.toggle('is-extra-long', main.length > 48);

        const mainLine = document.createElement('span');
        mainLine.className = 'pv2-wine-detail__name-main';
        mainLine.textContent = main;
        element.append(mainLine);

        if (qualifier) {
          const qualifierLine = document.createElement('span');
          qualifierLine.className = 'pv2-wine-detail__name-qualifier';
          qualifierLine.textContent = qualifier;
          element.append(qualifierLine);
        }
      };

      unique(catalogue.wines.map((wine) => wine.region)).forEach((value) => region?.append(makeOption(value)));
      unique(catalogue.wines.map((wine) => wine.producer)).forEach((value) => producer?.append(makeOption(value)));
      unique(catalogue.wines.map((wine) => wine.vintage)).sort((a, b) => String(b).localeCompare(String(a))).forEach((value) => vintage?.append(makeOption(value)));
      unique(catalogue.wines.map((wine) => wine.type)).forEach((value) => type?.append(makeOption(value)));

      const closeDetail = () => {
        selectedKey = '';
        selectedWine = null;
        if (detail) detail.hidden = true;
        if (detailBackdrop) detailBackdrop.hidden = true;
        grid?.classList.remove('has-selection');
        grid?.querySelectorAll('.pv2-wine-card').forEach((card) => {
          card.classList.remove('is-selected');
          card.querySelector('.pv2-wine-card__trigger')?.setAttribute('aria-expanded', 'false');
        });
        syncPageLock();
      };

      const showDetail = (wine, article) => {
        const key = wineKey(wine);
        const image = imageFor(wine);
        selectedKey = key;
        selectedWine = wine;

        if (detailImage) {
          detailImage.hidden = !isImageProvided(image);
          detailImage.src = isImageProvided(image) ? image.src : '';
          detailImage.alt = `${wine.producer} ${wine.name} bottle`;
          detailImage.dataset.imageMatch = image.match || 'unknown';
        }
        if (detailPlaceholder) detailPlaceholder.hidden = isImageProvided(image);
        if (detailRegion) detailRegion.textContent = wine.region;
        renderWineDetailName(detailName, wine.name);
        if (detailProducer) detailProducer.textContent = wine.producer;
        if (detailVintage) detailVintage.textContent = wine.vintage;
        if (detailSize) detailSize.textContent = wine.size;
        if (detailStory) detailStory.textContent = producerStories.get(wine.producer) || 'A grower-led bottle selected by Patrimonio for its clarity, character and sense of place.';
        if (detailPrice) {
          detailPrice.hidden = !showPrices;
          detailPrice.textContent = showPrices ? formatPrice(wine.price) : '';
        }
        if (quantity) quantity.value = '1';
        const hasVariant = Number.isFinite(variantIdFor(wine));
        if (addOrder) {
          addOrder.disabled = !hasVariant;
          addOrder.textContent = hasVariant ? 'Add to cart' : 'Online product setup pending';
        }
        if (orderStatus) {
          orderStatus.textContent = hasVariant
            ? 'This bottle will be added to your secure Shopify cart.'
            : 'This catalogue bottle is not yet connected to a Shopify product and cannot be paid for online yet.';
        }

        if (detail) detail.hidden = false;
        if (detailBackdrop) detailBackdrop.hidden = false;
        if (detail) detail.scrollTop = 0;
        grid?.classList.add('has-selection');
        grid?.querySelectorAll('.pv2-wine-card').forEach((card) => {
          const selected = card === article;
          card.classList.toggle('is-selected', selected);
          card.querySelector('.pv2-wine-card__trigger')?.setAttribute('aria-expanded', String(selected));
        });
        syncPageLock();
        detailClose?.focus({ preventScroll: true });
      };

      const createCard = (wine) => {
        const article = document.createElement('article');
        article.className = 'pv2-wine-card';
        article.dataset.wineKey = wineKey(wine);

        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'pv2-wine-card__trigger';
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', detail?.id || 'pv2-wine-detail');

        const media = document.createElement('div');
        media.className = 'pv2-wine-card__media';
        const imageData = imageFor(wine);
        if (isImageProvided(imageData)) {
          const image = document.createElement('img');
          image.src = imageData.src;
          image.alt = `${wine.producer} ${wine.name} bottle`;
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = 520;
          image.height = 700;
          image.dataset.imageMatch = imageData.match || 'unknown';
          media.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'pv2-wine-card__placeholder';
          placeholder.textContent = 'To be provided';
          media.append(placeholder);
        }

        const body = document.createElement('div');
        body.className = 'pv2-wine-card__body';

        const top = document.createElement('div');
        top.className = 'pv2-wine-card__top';
        const regionText = document.createElement('span');
        regionText.className = 'pv2-wine-card__region';
        regionText.textContent = wine.region;
        top.append(regionText);

        const heading = document.createElement('h3');
        heading.className = 'pv2-wine-card__name';
        heading.textContent = wine.name;
        const details = document.createElement('div');
        details.className = 'pv2-wine-card__details';
        const vintageText = document.createElement('span');
        vintageText.className = 'pv2-wine-card__vintage';
        vintageText.textContent = wine.vintage;
        details.append(vintageText);
        if (showPrices) {
          const priceText = document.createElement('span');
          priceText.className = 'pv2-wine-card__price';
          priceText.textContent = formatPrice(wine.price);
          details.append(priceText);
        }

        body.append(top, heading, details);
        trigger.append(media, body);
        trigger.addEventListener('click', () => showDetail(wine, article));
        article.append(trigger);
        return article;
      };

      const render = () => {
        const query = search?.value.trim().toLowerCase() || '';
        const matches = catalogue.wines.filter((wine) => {
          const searchable = `${wine.name} ${wine.producer} ${wine.region}`.toLowerCase();
          return (!query || searchable.includes(query))
            && (!region?.value || wine.region === region.value)
            && (!producer?.value || wine.producer === producer.value)
            && (!vintage?.value || wine.vintage === vintage.value)
            && (!type?.value || wine.type === type.value);
        });
        if (grid) {
          grid.replaceChildren(...matches.slice(0, visible).map(createCard));
          if (selectedKey) {
            const selectedCard = [...grid.querySelectorAll('.pv2-wine-card')].find((card) => card.dataset.wineKey === selectedKey);
            grid.classList.toggle('has-selection', Boolean(selectedCard));
            selectedCard?.classList.add('is-selected');
            selectedCard?.querySelector('.pv2-wine-card__trigger')?.setAttribute('aria-expanded', 'true');
          }
        }
        if (more) more.hidden = matches.length <= visible;
      };

      const restoreLocationHash = () => {
        const targetId = decodeURIComponent(window.location.hash.slice(1));
        if (!targetId) return;
        const target = document.getElementById(targetId);
        target?.scrollIntoView({ behavior: 'auto', block: 'start' });
      };

      [search, region, producer, vintage, type].filter(Boolean).forEach((field) => {
        field.addEventListener(field === search ? 'input' : 'change', () => {
          closeDetail();
          visible = 8;
          render();
        });
      });
      more?.addEventListener('click', () => {
        visible += 8;
        render();
      });
      detailClose?.addEventListener('click', closeDetail);
      detailBackdrop?.addEventListener('click', closeDetail);
      orderCartClose?.addEventListener('click', closeOrderCart);
      orderCartBackdrop?.addEventListener('click', closeOrderCart);
      quantityMinus?.addEventListener('click', () => {
        if (quantity) quantity.value = String(clampQuantity(Number(quantity.value) - 1));
      });
      quantityPlus?.addEventListener('click', () => {
        if (quantity) quantity.value = String(clampQuantity(Number(quantity.value) + 1));
      });
      quantity?.addEventListener('change', () => {
        quantity.value = String(clampQuantity(quantity.value));
      });
      addOrder?.addEventListener('click', async () => {
        if (!selectedWine) return;
        const amount = clampQuantity(quantity?.value);
        const variantId = variantIdFor(selectedWine);
        if (!Number.isFinite(variantId)) {
          if (orderStatus) orderStatus.textContent = 'Online checkout is pending Shopify product setup for this bottle.';
          return;
        }
        addOrder.disabled = true;
        if (orderStatus) orderStatus.textContent = 'Adding this bottle to your secure Shopify cart…';
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: [{
                id: variantId,
                quantity: amount,
                properties: {
                  'Catalogue producer': selectedWine.producer,
                  'Catalogue vintage': selectedWine.vintage
                }
              }]
            })
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || `Cart add failed (${response.status})`);
          }
          await loadShopifyCart();
          if (orderStatus) orderStatus.textContent = `${amount} bottle${amount === 1 ? '' : 's'} added to your Shopify cart.`;
          window.setTimeout(openOrderCart, 280);
        } catch (error) {
          addOrder.disabled = false;
          if (orderStatus) orderStatus.textContent = 'This bottle could not be added. Please try again or contact Patrimonio.';
          console.warn('Patrimonio Shopify cart add failed.', error);
        }
      });
      orderCheckout?.addEventListener('click', async (event) => {
        if (!shopifyCart.items?.length) {
          event.preventDefault();
          if (orderSetup) orderSetup.textContent = 'Add a Shopify-connected bottle before proceeding to checkout.';
          return;
        }
        event.preventDefault();
        const singaporeHour = Number(new Intl.DateTimeFormat('en-SG', {
          hour: '2-digit', hourCycle: 'h23', timeZone: 'Asia/Singapore'
        }).format(new Date()));
        const deliveryTiming = singaporeHour < 12
          ? 'Order placed before 12 PM — next-day delivery requested'
          : 'Order placed after 12 PM — following working-day delivery requested';
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
              attributes: {
                'Delivery region': 'Singapore only',
                'Delivery timing': deliveryTiming
              }
            })
          });
        } catch (error) {
          console.warn('Patrimonio delivery note could not be saved.', error);
        }
        window.location.assign('/checkout');
      });
      document.addEventListener('click', (event) => {
        const cartLink = event.target.closest('[data-pv2-cart-open]');
        if (!cartLink) return;
        event.preventDefault();
        openOrderCart(cartLink);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (orderCart && !orderCart.hidden) closeOrderCart();
        else if (selectedKey) closeDetail();
      });
      Promise.allSettled([loadShopifyCart(), loadShopifyProductMap()]).then((results) => {
        if (results[0].status === 'rejected') {
          console.warn('Patrimonio Shopify cart could not be initialized.', results[0].reason);
        }
        if (results[1].status === 'rejected') {
          console.warn('Patrimonio Shopify product map could not be initialized.', results[1].reason);
        }
        renderOrderCart();
        render();
        if (window.location.hash) {
          window.requestAnimationFrame(() => window.requestAnimationFrame(restoreLocationHash));
        }
      });
    });

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      document.querySelectorAll('.pv2-reveal').forEach((element) => observer.observe(element));
    } else {
      document.querySelectorAll('.pv2-reveal').forEach((element) => element.classList.add('is-visible'));
    }
  });
})();
